import { useEffect, useRef } from 'react'
import GameScene from './game3d/GameScene'
import Modal from './Modal'
import { useBotBattle } from '../hooks/useBotBattle'

interface BotBattleArenaProps {
  onBack: () => void
}

const DIFFICULTY_LABELS: Record<'easy' | 'medium' | 'hard' | 'advanced', string> = {
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
  advanced: 'Advanced',
}

export default function BotBattleArena({ onBack }: BotBattleArenaProps) {
  const battle = useBotBattle()
  const didStart = useRef(false)

  useEffect(() => {
    if (didStart.current) return
    didStart.current = true
    void battle.startNewGame()
  }, [battle])

  const { botBattle, winner, pieces, animatingPiece, isPaused, togglePause, currentPlayer, isAIThinking } = battle

  // The first mover always plays Red (player 1); the toggle decides which bot that is.
  const bot1Player = botBattle.bot1First ? 1 : 2
  const difficultyFor = (player: number) =>
    player === bot1Player ? botBattle.bot1Difficulty : botBattle.bot2Difficulty

  const describedPlayer = winner !== null && winner > 0 ? winner : currentPlayer
  const finished = winner !== null

  const statusText = finished
    ? winner > 0
      ? `${DIFFICULTY_LABELS[difficultyFor(winner)]} Bot wins`
      : 'Draw'
    : isPaused
      ? 'Paused'
      : `${DIFFICULTY_LABELS[difficultyFor(currentPlayer)]} Bot is thinking`

  const rematch = () => {
    void battle.startNewGame()
  }

  return (
    <main className="game-screen">
      <GameScene
        pieces={pieces}
        marbleStyle="alley-glass"
        selectedPiece={null}
        validMoves={[]}
        animatingPiece={animatingPiece}
        onPositionClick={() => {}}
      />

      <div className="game-overlay">
        <div className={`status-pill ${finished ? 'status-pill-win' : ''}`}>
          <span className={`turn-dot turn-dot-${describedPlayer}`} aria-hidden="true" />
          {statusText}
          {!finished && !isPaused && isAIThinking && (
            <span className="thinking-dots" aria-hidden="true">…</span>
          )}
        </div>

        <div className="overlay-actions">
          <button className="wood-button" type="button" onClick={togglePause} disabled={finished}>
            {isPaused ? 'Play' : 'Pause'}
          </button>
        </div>
      </div>

      {finished && (
        <Modal
          title={winner > 0 ? `${statusText}!` : 'Draw'}
          actions={
            <>
              <button className="sidebar-primary" type="button" onClick={rematch}>
                Watch Rematch
              </button>
              <button className="sidebar-option" type="button" onClick={onBack}>
                Main Menu
              </button>
            </>
          }
        >
          <p className="howto-lead">
            {winner > 0
              ? `${DIFFICULTY_LABELS[difficultyFor(winner)]} (${winner === 1 ? 'Red' : 'Blue'}) filled the goal first.`
              : 'Neither bot could finish.'}
          </p>
        </Modal>
      )}

      {!finished && isPaused && (
        <Modal
          title="Paused"
          onClose={togglePause}
          actions={
            <>
              <button className="sidebar-primary" type="button" onClick={togglePause}>
                Resume
              </button>
              <button className="sidebar-option" type="button" onClick={rematch}>
                Rematch
              </button>
              <button className="sidebar-option" type="button" onClick={onBack}>
                Main Menu
              </button>
            </>
          }
        />
      )}
    </main>
  )
}
